import { supabase } from '../../supabase.js';
import { validateAndGetTenantUuid } from '../../utils/tenantUtils.js';
import { ServiceError } from '../../utils/errorHandler.js';

class TokenService {
  constructor() {
    this.tokenPurchasesTable = 'token_purchases';
    this.tokenUsageLogsTable = 'token_usage_logs';
    this.userAppAccessTable = 'user_app_access';
  }

  // Purchase tokens
  async purchaseTokens(userId, tenantId, tokenAmount, costAmount, purchaseType = 'one_time', paymentMethod = 'credit_card') {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const purchaseData = {
        user_id: userId,
        tenant_id: tenantUuid,
        purchase_type: purchaseType,
        token_amount: tokenAmount,
        cost_amount: costAmount,
        payment_method: paymentMethod,
        payment_status: 'completed', // For demo purposes, assume payment is successful
        transaction_id: `TXN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        purchase_date: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from(this.tokenPurchasesTable)
        .insert(purchaseData)
        .select()
        .single();
      
      if (error) throw error;
      
      // Update user's token balance
      await this.addTokensToUser(userId, tenantId, tokenAmount);
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('TOKEN_PURCHASE_FAILED', error.message);
    }
  }

  // Add tokens to user's balance
  async addTokensToUser(userId, tenantId, tokenAmount) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      // Get current subscription
      const { data: subscription, error: subError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('tenant_id', tenantUuid)
        .eq('status', 'active')
        .single();
      
      if (subError && subError.code !== 'PGRST116') throw subError;
      
      if (subscription) {
        // Update existing subscription
        const { error } = await supabase
          .from('user_subscriptions')
          .update({
            tokens_allocated: subscription.tokens_allocated + tokenAmount,
            tokens_remaining: subscription.tokens_remaining + tokenAmount,
            updated_at: new Date().toISOString()
          })
          .eq('id', subscription.id);
        
        if (error) throw error;
      } else {
        // Create new subscription with tokens
        const { error } = await supabase
          .from('user_subscriptions')
          .insert({
            user_id: userId,
            tenant_id: tenantUuid,
            plan_id: null, // No specific plan
            status: 'active',
            start_date: new Date().toISOString().split('T')[0],
            tokens_allocated: tokenAmount,
            tokens_used: 0,
            tokens_remaining: tokenAmount,
            auto_renew: false
          });
        
        if (error) throw error;
      }
      
      return { success: true };
    } catch (error) {
      throw new ServiceError('TOKEN_BALANCE_UPDATE_FAILED', error.message);
    }
  }

  // Consume tokens for app usage
  async consumeTokens(userId, appId, tenantId, tokensToConsume, actionType = 'usage', sessionId = null, usageDetails = {}) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      // Check if user has enough tokens
      const userBalance = await this.getUserTokenBalance(userId, tenantId);
      if (userBalance.data.tokens_remaining < tokensToConsume) {
        throw new Error('Insufficient tokens');
      }
      
      // Log token usage
      const usageData = {
        user_id: userId,
        app_id: appId,
        tenant_id: tenantUuid,
        session_id: sessionId,
        action_type: actionType,
        tokens_consumed: tokensToConsume,
        usage_details: usageDetails,
        cost_amount: tokensToConsume * 0.02, // $0.02 per token
        created_at: new Date().toISOString()
      };
      
      const { data: usageLog, error: usageError } = await supabase
        .from(this.tokenUsageLogsTable)
        .insert(usageData)
        .select()
        .single();
      
      if (usageError) throw usageError;
      
      // Update user's token balance
      const { error: updateError } = await supabase
        .from('user_subscriptions')
        .update({
          tokens_used: userBalance.data.tokens_used + tokensToConsume,
          tokens_remaining: userBalance.data.tokens_remaining - tokensToConsume,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('tenant_id', tenantUuid)
        .eq('status', 'active');
      
      if (updateError) throw updateError;
      
      // Update app access record
      await this.updateAppAccessRecord(userId, appId, tenantId, tokensToConsume);
      
      return { success: true, data: usageLog };
    } catch (error) {
      throw new ServiceError('TOKEN_CONSUMPTION_FAILED', error.message);
    }
  }

  // Get user's token balance
  async getUserTokenBalance(userId, tenantId) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('tokens_allocated, tokens_used, tokens_remaining')
        .eq('user_id', userId)
        .eq('tenant_id', tenantUuid)
        .eq('status', 'active')
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      
      const balance = data || {
        tokens_allocated: 0,
        tokens_used: 0,
        tokens_remaining: 0
      };
      
      return { success: true, data: balance };
    } catch (error) {
      throw new ServiceError('TOKEN_BALANCE_FETCH_FAILED', error.message);
    }
  }

  // Get user's token usage history
  async getUserTokenUsageHistory(userId, tenantId, limit = 50) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const { data, error } = await supabase
        .from(this.tokenUsageLogsTable)
        .select(`
          *,
          apps (
            app_name,
            app_category
          )
        `)
        .eq('user_id', userId)
        .eq('tenant_id', tenantUuid)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('TOKEN_USAGE_HISTORY_FETCH_FAILED', error.message);
    }
  }

  // Get user's token purchase history
  async getUserTokenPurchaseHistory(userId, tenantId, limit = 50) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const { data, error } = await supabase
        .from(this.tokenPurchasesTable)
        .select('*')
        .eq('user_id', userId)
        .eq('tenant_id', tenantUuid)
        .order('purchase_date', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('TOKEN_PURCHASE_HISTORY_FETCH_FAILED', error.message);
    }
  }

  // Update app access record
  async updateAppAccessRecord(userId, appId, tenantId, tokensConsumed) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      // Check if app access record exists
      const { data: existingAccess, error: checkError } = await supabase
        .from(this.userAppAccessTable)
        .select('*')
        .eq('user_id', userId)
        .eq('app_id', appId)
        .eq('tenant_id', tenantUuid)
        .single();
      
      if (checkError && checkError.code !== 'PGRST116') throw checkError;
      
      if (existingAccess) {
        // Update existing record
        const { error } = await supabase
          .from(this.userAppAccessTable)
          .update({
            tokens_used: existingAccess.tokens_used + tokensConsumed,
            tokens_remaining: existingAccess.tokens_remaining - tokensConsumed,
            last_used_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', existingAccess.id);
        
        if (error) throw error;
      } else {
        // Create new app access record
        const { error } = await supabase
          .from(this.userAppAccessTable)
          .insert({
            user_id: userId,
            app_id: appId,
            tenant_id: tenantUuid,
            tokens_used: tokensConsumed,
            tokens_remaining: 0,
            last_used_at: new Date().toISOString()
          });
        
        if (error) throw error;
      }
      
      return { success: true };
    } catch (error) {
      throw new ServiceError('APP_ACCESS_UPDATE_FAILED', error.message);
    }
  }

  // Get token usage statistics for tenant
  async getTokenUsageStats(tenantId, dateRange = null) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      let query = supabase
        .from(this.tokenUsageLogsTable)
        .select('tokens_consumed, cost_amount, created_at')
        .eq('tenant_id', tenantUuid);
      
      if (dateRange) {
        query = query
          .gte('created_at', dateRange.start)
          .lte('created_at', dateRange.end);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      const stats = {
        total_tokens_consumed: data.reduce((sum, log) => sum + (log.tokens_consumed || 0), 0),
        total_cost: data.reduce((sum, log) => sum + (log.cost_amount || 0), 0),
        usage_count: data.length,
        average_tokens_per_usage: data.length > 0 ? 
          data.reduce((sum, log) => sum + (log.tokens_consumed || 0), 0) / data.length : 0
      };
      
      return { success: true, data: stats };
    } catch (error) {
      throw new ServiceError('TOKEN_USAGE_STATS_FETCH_FAILED', error.message);
    }
  }

  // Get token usage by app
  async getTokenUsageByApp(tenantId, appId = null) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      let query = supabase
        .from(this.tokenUsageLogsTable)
        .select(`
          tokens_consumed,
          cost_amount,
          apps (
            app_name,
            app_category
          )
        `)
        .eq('tenant_id', tenantUuid);
      
      if (appId) {
        query = query.eq('app_id', appId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Group by app
      const appUsage = data.reduce((acc, log) => {
        const appName = log.apps?.app_name || 'Unknown App';
        if (!acc[appName]) {
          acc[appName] = {
            app_name: appName,
            app_category: log.apps?.app_category,
            total_tokens: 0,
            total_cost: 0,
            usage_count: 0
          };
        }
        
        acc[appName].total_tokens += log.tokens_consumed || 0;
        acc[appName].total_cost += log.cost_amount || 0;
        acc[appName].usage_count += 1;
        
        return acc;
      }, {});
      
      return { success: true, data: Object.values(appUsage) };
    } catch (error) {
      throw new ServiceError('TOKEN_USAGE_BY_APP_FETCH_FAILED', error.message);
    }
  }
}

export const tokenService = new TokenService();
